/**
 * Design tokens — single source of truth for colors, typography, spacing, and radii.
 * Consumed by MUI theme (muiTheme.js) and exposed as CSS custom properties (global.css).
 */

export const colors = {
  primary: {
    main: '#1565C0',
    light: '#42A5F5',
    dark: '#0D47A1',
    contrastText: '#FFFFFF',
    50: '#E3F2FD',
    100: '#BBDEFB',
  },
  secondary: {
    main: '#5C6BC0',
    light: '#9FA8DA',
    dark: '#3949AB',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
    contrastText: '#FFFFFF',
    surface: '#E8F5E9',
  },
  warning: {
    main: '#ED6C02',
    light: '#FF9800',
    dark: '#E65100',
    contrastText: '#FFFFFF',
    surface: '#FFF3E0',
  },
  error: {
    main: '#D32F2F',
    light: '#EF5350',
    dark: '#C62828',
    contrastText: '#FFFFFF',
    surface: '#FFEBEE',
  },
  info: {
    main: '#0288D1',
    light: '#03A9F4',
    dark: '#01579B',
    contrastText: '#FFFFFF',
    surface: '#E1F5FE',
  },
  neutral: {
    0: '#FFFFFF',
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  text: {
    primary: '#1E293B',
    secondary: '#475569',
    disabled: '#94A3B8',
    inverse: '#FFFFFF',
  },
  background: {
    default: '#F1F5F9',
    paper: '#FFFFFF',
    subtle: '#F8FAFC',
    elevated: '#FFFFFF',
  },
  border: {
    default: '#E2E8F0',
    strong: '#CBD5E1',
    focus: '#1565C0',
  },
  action: {
    hover: 'rgba(21, 101, 192, 0.04)',
    selected: 'rgba(21, 101, 192, 0.08)',
    disabled: 'rgba(30, 41, 59, 0.26)',
    disabledBackground: 'rgba(30, 41, 59, 0.12)',
  },
  sidebar: {
    background: '#FFFFFF',
    border: '#E2E8F0',
    active: '#1565C0',
    activeBackground: '#E3F2FD',
    text: '#334155',
    textMuted: '#64748B',
  },
};

export const typography = {
  fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
  fontFamilyMono: '"JetBrains Mono", "Fira Code", "Consolas", monospace',
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  fontSize: {
    xs: 11,
    sm: 12,
    base: 14,
    md: 16,
    lg: 18,
    xl: 20,
    '2xl': 24,
    '3xl': 28,
    '4xl': 32,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
  letterSpacing: {
    tight: '-0.01em',
    normal: '0',
    wide: '0.02em',
    wider: '0.05em',
  },
};

/** 8px base grid — factor × 8px */
export const spacingUnit = 8;

export const spacing = {
  0: 0,
  0.5: 4,
  1: 8,
  1.5: 12,
  2: 16,
  2.5: 20,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  8: 64,
  10: 80,
  12: 96,
};

export const radii = {
  none: 0,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  '2xl': 20,
  pill: 9999,
  full: '50%',
};

export const shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(15, 23, 42, 0.05)',
  sm: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  md: '0 4px 6px -1px rgba(15, 23, 42, 0.08), 0 2px 4px -2px rgba(15, 23, 42, 0.04)',
  lg: '0 10px 15px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -4px rgba(15, 23, 42, 0.04)',
  xl: '0 20px 25px -5px rgba(15, 23, 42, 0.08), 0 8px 10px -6px rgba(15, 23, 42, 0.04)',
};

export const layout = {
  sidebarWidthCollapsed: 56,
  sidebarWidthExpanded: 216,
  headerHeight: 56,
  contentMaxWidth: 1440,
};

export const zIndex = {
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
  toast: 1400,
};

export const transitions = {
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
};

const tokens = {
  colors,
  typography,
  spacingUnit,
  spacing,
  radii,
  shadows,
  layout,
  zIndex,
  transitions,
};

export default tokens;
